// Site & General — per-template authored content. See ./types.ts for the content rule.
import type { TemplateSpecific } from "./types";

export const SITE_GENERAL: Record<string, TemplateSpecific> = {
  "site-setup": {
    whoFor:
      "Principal contractors and site managers establishing a new construction site need this RAMS before anyone starts work proper. A client or PC will almost always ask to see how you intend to enclose the site, connect services and provide compliant welfare from day one, because welfare and public protection are the things an HSE inspector checks first on a visit. If your site set up risk assessment cannot show CDM-compliant facilities and a secure perimeter, mobilisation gets held up.",
    scope:
      "This RAMS covers the establishment phase of a construction site: erecting the perimeter hoarding or Heras fencing, installing site signage, positioning welfare and office units, and making temporary service connections. It runs from first delivery of cabins to the point the site is secured, signed and welfare is operational. It does not cover the subsequent construction works, demolition or any deep excavation for permanent foundations, which are the subject of their own method statements. Securing the site against children and trespass during and after establishment is treated as a primary control throughout.",
    legislation: [
      "Construction (Design and Management) Regulations 2015 — Regulation 13 and Schedule 2 (welfare facilities)",
      "Health and Safety at Work etc. Act 1974, Section 3 (duties to non-employees)",
      "HSG151 Protecting the public — your next move",
      "Workplace (Health, Safety and Welfare) Regulations 1992",
      "Electricity at Work Regulations 1989",
    ],
    sequence: [
      "Survey the site boundary, confirm underground services with utility records and a CAT scan, and agree the access/egress points with the client.",
      "Erect the perimeter — solid hoarding to the public-facing elevations and Heras fencing elsewhere — braced and weighted against wind loading, with lockable gates.",
      "Install statutory and warning signage at the entrance: site rules, PPE requirements, emergency contacts and the F10 notification where required.",
      "Position and level the welfare, office and drying units, then make the temporary electrical, water and foul-drainage connections through a competent person.",
      "Commission welfare to CDM Schedule 2 — toilets, washing with hot and cold water, rest area, and a means of heating food and drying clothing — before any other trade arrives.",
      "Manage establishment-phase deliveries with a banksman and a one-way traffic route, keeping the public footway clear and protected at all times.",
      "Carry out a close-of-day security check, ensuring gates are locked and the perimeter is intact to prevent unauthorised access by children or trespassers overnight.",
    ],
    hazards: [
      {
        hazard: "Unauthorised access by children or trespassers to a part-established site",
        who: ["Members of the public", "Children", "Trespassers"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Maintain a continuous, climb-resistant perimeter with no gaps left open during establishment",
          "Lock all gates and immobilise plant at the end of each working day",
          "Remove or fence off attractive hazards such as ladders, stacked materials and open excavations",
          "Carry out and record a daily perimeter security check before leaving site",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Hoarding or temporary fencing collapse under wind loading",
        who: ["Members of the public", "Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Design and brace hoarding for site-specific wind loads using a competent supplier",
          "Use stable bases and couplers rather than free-standing panels on public elevations",
          "Inspect the perimeter after high winds and reinstate any displaced panels immediately",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
      {
        hazard: "Electric shock during temporary service connection",
        who: ["Operatives", "Electricians"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Have all temporary electrical connections made and certified by a competent electrician",
          "Isolate and prove dead before working on incoming supplies; apply lock-off",
          "Use a temporary distribution system with RCD protection and a 110V site supply",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Inadequate welfare leading to ill health and poor hygiene",
        who: ["Operatives", "Visitors"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Provide CDM Schedule 2 welfare — toilets, washing, rest, drying — before works begin",
          "Connect hot and cold running water and maintain a regular cleaning and stocking regime",
          "Size facilities to the expected peak workforce and review as numbers grow",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
      {
        hazard: "Striking buried services while fixing fence posts or unit foundations",
        who: ["Operatives", "Plant operators"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Obtain utility drawings and CAT-and-Genny scan all post and base locations",
          "Hand-dig trial holes to confirm depth and position of any services found",
          "Use ground-bearing bases or driven posts only where services are proven clear",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Hard hat to EN 397",
      "Hi-vis clothing to EN ISO 20471",
      "Safety boots with toe and midsole protection",
      "Gloves appropriate to manual handling and fencing",
      "Eye protection when cutting hoarding or fixings",
    ],
    competence: [
      "Site manager with SMSTS",
      "Operatives with valid CSCS cards",
      "Competent electrician (NICEIC/NAPIT) for temporary supplies",
      "Banksman trained for delivery vehicle marshalling",
    ],
    permits: [
      "Permit to dig for any post or foundation excavation",
      "Hot works permit if cutting or grinding hoarding sections",
    ],
    equipment: [
      "Solid timber/ply hoarding and Heras fencing panels with bases and couplers",
      "Site welfare, office and drying cabins",
      "Temporary electrical distribution board with RCD protection",
      "CAT and Genny cable avoidance tool",
      "Telehandler or HIAB for unloading and positioning units",
      "Statutory signage boards and fixings",
    ],
    pcChecks: [
      "That the welfare provision is sized and specified against CDM Schedule 2 for the peak workforce, not just 'welfare provided'",
      "That the perimeter and out-of-hours security arrangements specifically address access by children, given the public location",
      "That temporary electrical connections are assigned to a named competent person with certification, not left to 'site team'",
    ],
    faqs: [
      {
        q: "How many toilets does a construction site need?",
        a: "CDM 2015 Schedule 2 requires sufficient sanitary conveniences for the number of people on site, with washing facilities adjacent. HSE guidance commonly cited is one toilet and one urinal for up to 7 people, rising with headcount; you should size against your expected peak workforce. Where flushing toilets cannot connect to a sewer, use a self-contained or chemical unit that is regularly emptied and cleaned. Separate facilities or lockable single units must be provided where women work on site.",
      },
      {
        q: "Do I need solid hoarding or is Heras fencing enough?",
        a: "It depends on the public exposure. Solid hoarding is normally expected to public-facing elevations, busy footways and town-centre sites because it stops materials and dust escaping and deters access, which HSG151 treats as protecting the public. Heras fencing can be acceptable on lower-risk boundaries away from the public, provided it is braced, weighted and climb-resistant. Your risk assessment should justify the choice for each boundary rather than applying one type everywhere.",
      },
    ],
  },

  "site-occupied": {
    whoFor:
      "Contractors carrying out refurbishment, fit-out or maintenance in a building that stays open need this RAMS to show how they will keep occupants, staff and the public safe alongside the works. Hospitals, schools, care homes, offices and retail units cannot simply be cleared, so the client and PC will expect a method statement that proves segregation, fire-escape protection and nuisance control before they let you start. Section 3 of the Health and Safety at Work Act makes your duty to those non-employees a legal one, not a courtesy.",
    scope:
      "This RAMS covers construction, refurbishment and maintenance works carried out in or adjacent to premises that remain occupied and operational during the works. It addresses physical and acoustic segregation from occupants, control of dust and noise nuisance, protection of fire-escape routes, out-of-hours working and safeguarding in sensitive settings such as schools and care homes. It does not cover the technical detail of each trade activity, which sits in the relevant trade method statement. Liaison with the occupier and agreement of working hours and exclusion zones is treated as a precondition of the works.",
    legislation: [
      "Health and Safety at Work etc. Act 1974, Section 3 (duties to non-employees)",
      "Construction (Design and Management) Regulations 2015",
      "Regulatory Reform (Fire Safety) Order 2005",
      "Control of Dust — Control of Substances Hazardous to Health Regulations 2002 (COSHH)",
      "HSG151 Protecting the public — your next move",
    ],
    sequence: [
      "Liaise with the occupier to agree working hours, access routes, exclusion zones and any safeguarding or DBS requirements before mobilising.",
      "Erect physical segregation — dust-proof screens, hoardings or temporary partitions — to separate the work area from occupied space.",
      "Confirm and protect all fire-escape routes serving occupants, keeping them clear and signed at all times and agreeing alternatives if any are affected.",
      "Set up dust and noise nuisance controls: local extraction, damping down, and scheduling of the noisiest tasks to agreed quiet periods or out of hours.",
      "Brief operatives on the occupier's rules, the boundary they must not cross, and safeguarding expectations where children or vulnerable adults are present.",
      "Carry out the works within the segregated zone, controlling deliveries and waste removal along agreed routes away from occupants.",
      "At the end of each shift, clean down shared areas, confirm escape routes are clear and hand the area back to the occupier in a safe condition.",
    ],
    hazards: [
      {
        hazard: "Obstruction of occupants' fire-escape routes",
        who: ["Building occupants", "Staff", "Members of the public"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Keep all designated escape routes clear of materials, plant and waste at all times",
          "Agree and sign any temporary alternative route with the occupier's responsible person",
          "Do not store or stage materials in corridors, stairs or final exits at any point in the shift",
          "Check escape routes are clear at every break and at end of shift, and record it",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Occupants entering the live work area",
        who: ["Building occupants", "Children", "Vulnerable adults"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Provide a continuous, lockable physical barrier between works and occupied space",
          "Display clear warning signage and keep access doors closed and controlled",
          "Brief occupier staff so they reinforce the boundary with their own people",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Dust and noise nuisance affecting occupant health and operations",
        who: ["Building occupants", "Staff", "Operatives"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Schedule the dustiest and noisiest tasks to agreed quiet periods or out-of-hours slots",
          "Use on-tool extraction and damping down to suppress dust at source",
          "Seal screens and protect ventilation intakes to stop dust migrating into occupied areas",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Safeguarding failure in a school or care setting",
        who: ["Children", "Vulnerable adults", "Operatives"],
        initial: { likelihood: 2, severity: 4 },
        controls: [
          "Obtain DBS checks for operatives where required and verify before site access",
          "Keep operatives within the segregated zone and escort any movement through occupied areas",
          "Brief the team on the occupier's safeguarding policy and reporting routes",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
      {
        hazard: "Out-of-hours lone or small-team working in an occupied building",
        who: ["Operatives", "Security staff"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Agree out-of-hours access, sign-in and security cover with the occupier in advance",
          "Establish a check-in arrangement and emergency contact for staff working after hours",
          "Confirm fire alarm coverage and escape lighting remain active during the works",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
    ],
    ppe: [
      "Hard hat to EN 397 where overhead risk exists",
      "Hi-vis clothing to EN ISO 20471",
      "FFP3 mask for dust-generating tasks",
      "Safety footwear",
      "Hearing protection during noisy operations",
    ],
    competence: [
      "Site supervisor with SSSTS or SMSTS",
      "Operatives with valid CSCS cards",
      "Enhanced DBS where working in schools or care settings",
      "Fire-marshal-trained supervisor for the works area",
    ],
    permits: [
      "Hot works permit for any cutting, grinding or soldering near occupants",
      "Out-of-hours working permit agreed with the occupier",
    ],
    equipment: [
      "Dust-proof temporary screens and zip-door partition systems",
      "On-tool dust extraction (H/M-class vacuums)",
      "Air movers and negative-pressure units where required",
      "Temporary fire-route signage and escape lighting checks",
      "Floor and surface protection for occupant circulation routes",
      "Acoustic blankets for noise screening",
    ],
    pcChecks: [
      "That fire-escape routes serving occupants are named and protected, with an agreed alternative if any route is affected — not a blanket 'keep exits clear'",
      "That dust and noise controls are tied to agreed quiet periods and the occupier's operations, with named liaison arrangements",
      "That safeguarding and DBS requirements for the specific setting are stated where children or vulnerable adults are present",
    ],
    faqs: [
      {
        q: "Can we keep a building open while we work in it?",
        a: "Yes, but only if you can physically and acoustically separate the works from occupants and keep their fire-escape routes clear at all times. Your duty under Section 3 of the Health and Safety at Work Act is to ensure those non-employees are not exposed to risk, which HSG151 expands on for the public. That means agreed exclusion zones, dust and noise control, and a written liaison arrangement with the occupier. If escape routes cannot be protected, the area should be closed or the works rephased.",
      },
      {
        q: "Do operatives need DBS checks to work in an occupied school?",
        a: "Where the work brings operatives into contact with children, or unsupervised access to areas children use, enhanced DBS checks are normally required and the school will ask to see them. If operatives are kept fully within a segregated, supervised zone with no contact, checks may not be needed, but you must agree this with the school in advance. The safe default is to confirm the safeguarding requirement during occupier liaison before mobilising. Working out of hours when pupils are absent can reduce, but does not always remove, the requirement.",
      },
    ],
  },

  "site-lone-working": {
    whoFor:
      "Any employer whose staff carry out tasks alone — surveyors, maintenance engineers, callout operatives or trades on small jobs — needs a lone working RAMS that sets out how those workers are kept safe without a colleague present. This is a procedures document: clients and PCs want to see check-in schedules, escalation and the tasks you forbid alone, not a generic equipment list. HSE guidance INDG73 makes clear that lone working is not prohibited but must be specifically risk-assessed.",
    scope:
      "This RAMS covers the arrangements for managing employees who work alone, whether on a fixed site, travelling between sites, or at a client's premises out of normal hours. It defines check-in schedules, lone-worker devices and man-down alarms, emergency escalation, and the categories of work that must never be carried out alone. It does not authorise any high-risk task to be done solo — those remain prohibited and require a second person. The procedure applies from the moment a worker is dispatched alone to the point they confirm safe completion.",
    legislation: [
      "Management of Health and Safety at Work Regulations 1999 (suitable and sufficient risk assessment)",
      "Health and Safety at Work etc. Act 1974, Section 2",
      "HSE INDG73 Protecting lone workers — how to manage the risks of working alone",
      "Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013 (RIDDOR)",
      "First Aid Regulations 1981",
    ],
    sequence: [
      "Risk-assess the specific task and location, and confirm it is not on the prohibited-alone list before dispatching the worker.",
      "Agree the check-in schedule and method — periodic calls, app check-ins or a monitored lone-worker device — and the expected completion time.",
      "Issue the worker a charged lone-worker device with man-down and panic functions, and confirm signal coverage at the location.",
      "Worker confirms arrival, completes the task within the assessed scope, and makes check-ins at the agreed intervals.",
      "If a check-in is missed, the monitor follows the escalation procedure: attempt contact, then dispatch a responder, then call emergency services.",
      "Worker confirms safe completion and stand-down; the monitor records the close-out and any incidents for RIDDOR review.",
    ],
    hazards: [
      {
        hazard: "Worker incapacitated with no one aware (accident, collapse, assault)",
        who: ["Lone workers"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Operate a monitored check-in schedule with a defined missed-check-in escalation",
          "Issue a lone-worker device with automatic man-down and manual panic alarm",
          "Confirm mobile/GPS coverage at the location and provide a fallback contact method",
          "Hold next-of-kin and location details so responders can be dispatched quickly",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "High-risk task attempted alone (live electrical, confined space, work at height)",
        who: ["Lone workers"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Prohibit live electrical work, confined-space entry and at-height work where rescue is impossible from being done alone",
          "Require a second competent person or rescue arrangement before such work proceeds",
          "Empower the worker to stop and reschedule if the task turns out to need two people",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Violence or aggression toward a lone worker",
        who: ["Lone workers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Screen locations for known aggression risk and avoid lone dispatch where flagged",
          "Provide a panic alarm and a discreet duress check-in word",
          "Brief workers on de-escalation and on withdrawing rather than confronting",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Inability to summon help due to remote location or lost signal",
        who: ["Lone workers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Verify communications coverage before dispatch and provide a satellite or radio backup where none exists",
          "Shorten check-in intervals for remote locations",
          "Log a precise location and route so a responder can find the worker without phone contact",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
    ],
    ppe: [
      "Lone-worker alarm device with man-down function",
      "Charged mobile phone with emergency contacts",
      "Task-appropriate PPE for the activity being carried out",
      "First-aid kit suited to the work and location",
    ],
    competence: [
      "Worker assessed as competent and confident to work unsupervised",
      "Trained in the use of the lone-worker device and escalation procedure",
      "First-aid trained where the location is remote from help",
      "Monitor/controller trained to follow the escalation procedure",
    ],
    permits: [
      "Authorisation-to-work-alone sign-off confirming the task is not on the prohibited list",
    ],
    equipment: [
      "Monitored lone-worker device (man-down, panic, GPS)",
      "Mobile phone and, where signal is poor, satellite communicator or two-way radio",
      "Check-in logging system or app at the monitoring end",
      "Personal first-aid kit",
      "Vehicle with breakdown cover for travelling workers",
    ],
    pcChecks: [
      "That the RAMS names the specific tasks prohibited from being done alone — live electrical, confined space, rescue-impossible work at height — not just 'be careful when alone'",
      "That a real, monitored check-in schedule with a defined missed-check-in escalation exists, with someone named to receive it",
      "That signal/communications coverage at the actual location has been confirmed, with a fallback where it is poor",
    ],
    faqs: [
      {
        q: "Can an apprentice work alone?",
        a: "No, an apprentice should not be working unsupervised. The Management of Health and Safety at Work Regulations require young or inexperienced workers to be given extra protection, and lone working removes the supervision and immediate help an apprentice relies on. Apprentices should work within sight or earshot of a competent colleague who can intervene. Lone working should be reserved for workers assessed as experienced and confident enough to handle the task and any emergency alone.",
      },
      {
        q: "What tasks should never be done by a lone worker?",
        a: "Any task where an incident would leave the worker unable to summon help, or where rescue needs more than one person. The common prohibitions are live electrical work, confined-space entry, and work at height where self-rescue is impossible, because all three can incapacitate a worker who then cannot call for help. These require a second competent person or a planned rescue arrangement before they proceed. Your lone working procedure should list these explicitly so there is no ambiguity on site.",
      },
    ],
  },

  "site-noise-havs": {
    whoFor:
      "Contractors using breakers, grinders, cut-off saws, needle guns and other vibrating or noisy tools need this RAMS to show they are managing exposure to the legal action and limit values, not just handing out ear defenders. A PC reviewing a havs risk assessment will look for trigger-time budgeting and health surveillance, because hand-arm vibration syndrome is irreversible and a reportable disease under RIDDOR. The Noise and Vibration at Work Regulations make exposure management a measurable duty, not a discretionary one.",
    scope:
      "This RAMS covers the assessment and control of exposure to noise and hand-arm vibration arising from powered hand tools and plant on site — breaking, grinding, cutting, drilling and compaction. It sets out exposure action and limit values, trigger-time budgeting per tool, low-vibration tool selection and health surveillance. It does not cover whole-body vibration from ride-on plant, which is assessed separately. The controls apply wherever the listed tools are used and to every operative who uses them, including occasional users.",
    legislation: [
      "Control of Vibration at Work Regulations 2005 (EAV 2.5 m/s², ELV 5.0 m/s² A(8))",
      "Control of Noise at Work Regulations 2005 (lower EAV 80 dB(A), upper EAV 85 dB(A))",
      "Management of Health and Safety at Work Regulations 1999",
      "HSE L140 Hand-arm vibration guidance",
      "Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013 (RIDDOR — HAVS is reportable)",
    ],
    sequence: [
      "Identify every noisy or vibrating tool for the task and obtain its vibration magnitude and noise data from the manufacturer or HSE database.",
      "Calculate each operative's daily exposure and set a trigger-time budget per tool so the A(8) stays below the exposure action value.",
      "Select the lowest-vibration tool that will do the job, and replace blunt or worn consumables that increase vibration and run time.",
      "Issue the work with rotation built in so no one operative absorbs the full daily exposure, and brief operatives on their trigger-time limits.",
      "Provide hearing protection rated for the measured noise levels, and require it within designated hearing-protection zones.",
      "Monitor actual trigger times during the shift and stand operatives down from vibrating tools once their budget is reached.",
      "Enrol exposed operatives in health surveillance and act on any early HAVS or noise-induced hearing-loss findings.",
    ],
    hazards: [
      {
        hazard: "Hand-arm vibration syndrome from prolonged use of vibrating tools",
        who: ["Operatives", "Occasional tool users"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Eliminate or reduce vibration by choosing low-vibration tools and the right method for the job",
          "Budget trigger time per tool so the A(8) stays below the 2.5 m/s² exposure action value",
          "Rotate operatives so no individual exceeds their daily exposure budget",
          "Provide health surveillance and remove affected operatives from further exposure",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Noise-induced hearing loss from breaking, grinding and cutting",
        who: ["Operatives", "Adjacent workers"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Reduce noise at source with quieter tools, damped blades and barriers where practicable",
          "Designate hearing-protection zones above 85 dB(A) and enforce their use",
          "Provide hearing protection matched to the measured levels without over-protecting",
          "Include exposed operatives in audiometric health surveillance",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Exceeding the daily vibration exposure limit value (5.0 m/s² A(8))",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Calculate exposure in advance and stop tool use before the limit value is reached",
          "Split high-vibration tasks across operatives or days to keep within the limit",
          "Use a points-based ready-reckoner so supervisors can track exposure on the day",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Cold and grip force increasing the effect of vibration",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Keep hands warm and dry with anti-vibration gloves and warm-up breaks in cold weather",
          "Maintain tools so operatives need not grip harder to control them",
          "Train operatives to let the tool do the work rather than forcing it",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
    ],
    ppe: [
      "Hearing protection rated to the measured noise level",
      "Anti-vibration gloves (as a comfort/warmth measure, not a primary control)",
      "Eye protection for cutting and grinding",
      "FFP3 mask where the task also produces dust",
      "Warm clothing in cold conditions to maintain circulation",
    ],
    competence: [
      "Operatives trained in HAVS awareness and trigger-time management",
      "Supervisor competent to calculate and track A(8) exposures",
      "Occupational health provider for HAVS and audiometric surveillance",
      "Tool users trained in correct technique to minimise vibration",
    ],
    permits: [
      "Hot works permit where cutting or grinding creates ignition sources",
    ],
    equipment: [
      "Low-vibration breakers and pneumatic tools with quoted magnitudes",
      "Cut-off saws and angle grinders with vibration-damped handles",
      "Sharp, correctly specified blades and consumables",
      "HAVS exposure calculator or points ready-reckoner",
      "Sound level meter for zone verification",
      "Trigger-time logging cards or device",
    ],
    pcChecks: [
      "That a trigger-time budget per tool is calculated against the A(8) action value, not a blanket 'wear gloves and take breaks'",
      "That health surveillance is named for the exposed operatives, with a route to act on early HAVS findings",
      "That tool selection lists actual vibration magnitudes so the exposure sums can be checked",
    ],
    faqs: [
      {
        q: "What trigger time hits the HAVS action value?",
        a: "It depends entirely on the tool's vibration magnitude. The exposure action value of 2.5 m/s² A(8) is reached after about two hours of trigger time on a tool measuring 5 m/s², but after only 15 minutes on one measuring 10 m/s². That is why you must use the manufacturer's or HSE's magnitude figure and the HSE exposure calculator for each tool rather than a single rule of thumb. The action value triggers the need for controls and health surveillance; the limit value of 5.0 m/s² must not be exceeded at all.",
      },
      {
        q: "At what noise level must I provide hearing protection?",
        a: "Under the Control of Noise at Work Regulations 2005 the lower exposure action value is 80 dB(A) daily, at which you must make hearing protection available and provide information and training. At the upper action value of 85 dB(A) you must establish hearing-protection zones and ensure protection is worn. The exposure limit value of 87 dB(A), measured inside the ear protection, must never be exceeded. Hearing protection is the last line of defence — you should reduce noise at source first.",
      },
    ],
  },

  "site-temp-works": {
    whoFor:
      "Principal contractors and works contractors erecting falsework, formwork, propping, façade retention or trench support need this RAMS to demonstrate procedural control of temporary works under BS 5975. Clients and PCs will expect to see a temporary works register, named coordinator and supervisor roles, and the design-check category for each item, because temporary works failures are a recognised cause of major collapses. This is a management-of-process document: it controls how temporary works are designed, checked, loaded and struck, not the build method of any single element.",
    scope:
      "This RAMS covers the procedural management of temporary works on the project in line with BS 5975:2019 — the temporary works register, the appointment of a Temporary Works Coordinator and supervisors, the design-check categories, and the permits to load and to strike. It applies to all temporary works items including falsework, formwork, propping, trench support and façade retention. It does not contain the design of any individual temporary work, which is produced and checked separately; instead it controls how those designs are registered, checked and authorised on site. The procedure runs from identification of a temporary works item through to its safe removal.",
    legislation: [
      "BS 5975:2019 Code of practice for temporary works procedures",
      "Construction (Design and Management) Regulations 2015",
      "Health and Safety at Work etc. Act 1974",
      "BS EN 12812 (falsework) and BS EN 12811 (temporary works equipment) where applicable",
      "Work at Height Regulations 2005",
    ],
    sequence: [
      "The Temporary Works Coordinator (TWC) identifies each temporary works item and enters it on the temporary works register with a design brief.",
      "A design is produced and assigned a design-check category (0 to 3) according to its complexity and risk under BS 5975.",
      "An independent designer carries out the design check appropriate to the assigned category and records sign-off before any work proceeds.",
      "The Temporary Works Supervisor confirms the temporary works are erected in accordance with the checked design and inspection records are complete.",
      "The TWC issues a permit to load only when the erection and check are signed off; the structure is not loaded before that permit exists.",
      "The works proceed under the loaded temporary works, with inspections at the frequency set in the design and register.",
      "When the permanent works can support themselves, the TWC issues a permit to strike, and the temporary works are removed in the designed sequence.",
    ],
    hazards: [
      {
        hazard: "Premature loading of temporary works before design check and sign-off",
        who: ["Operatives", "Adjacent workers", "Members of the public"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Operate a permit-to-load system controlled solely by the TWC",
          "Withhold the permit until the design check for the assigned category is signed off",
          "Display the load status on the item so no operative loads it ahead of authorisation",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Temporary works erected not in accordance with the checked design",
        who: ["Operatives", "Adjacent workers"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Require the Temporary Works Supervisor to inspect against the design before sign-off",
          "Use the correct, registered components and reject substitutions not in the design",
          "Record an erection inspection for every item and rectify defects before loading",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Uncontrolled or out-of-sequence striking of temporary works",
        who: ["Operatives", "Adjacent workers", "Building users"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Issue a permit to strike only when the permanent works are confirmed self-supporting",
          "Follow the designed striking sequence and timing, including concrete strength criteria",
          "Keep operatives clear of the collapse zone during the striking operation",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Unregistered temporary works escaping the procedural control",
        who: ["Operatives", "Adjacent workers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Brief all trades that any temporary support must go through the TWC and onto the register",
          "Audit the works against the register to catch ad-hoc propping or excavation support",
          "Stop any temporary works found in use without a register entry and design check",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Hard hat to EN 397",
      "Hi-vis clothing to EN ISO 20471",
      "Safety footwear with midsole protection",
      "Gloves for handling components",
      "Harness where erecting or striking at height requires it",
    ],
    competence: [
      "Appointed Temporary Works Coordinator (TWC) with CITB TWC training",
      "Temporary Works Supervisor (TWS) with CITB TWS training",
      "Independent design checker competent for the assigned category",
      "Erectors competent in the specific temporary works system (e.g. CISRS for falsework access)",
    ],
    permits: [
      "Permit to load issued by the TWC",
      "Permit to strike issued by the TWC",
    ],
    equipment: [
      "Falsework and formwork systems with their design documentation",
      "Adjustable props and soldiers to the registered specification",
      "Trench support boxes or hydraulic shoring where applicable",
      "Temporary works register and permit documentation",
      "Survey/level equipment to confirm setting-out and deflection",
      "Torque tools where component tightening is specified",
    ],
    pcChecks: [
      "A named TWC and the design-check category (0-3) for each temporary works item — 'scaffold designer to confirm' is not acceptance criteria",
      "That permits to load and to strike are controlled by the TWC and tied to the design check, not issued by the gang on site",
      "That the temporary works register is in place and covers every item, including trench support and propping, not just the headline falsework",
    ],
    faqs: [
      {
        q: "What is a TWC?",
        a: "A TWC is a Temporary Works Coordinator, the role at the heart of the BS 5975 procedure. They maintain the temporary works register, ensure each item is designed and independently checked to the right category, and control the permits to load and to strike. The TWC does not design the temporary works themselves; they coordinate the process and authorise the key hold points. On larger sites they are supported by Temporary Works Supervisors who oversee erection and inspection on the ground.",
      },
      {
        q: "What are the design-check categories in BS 5975?",
        a: "BS 5975 sets out four design-check categories reflecting complexity and risk. Category 0 is restricted to standard solutions and very simple items checked by the designer; Category 1 is checked by someone independent of the designer but within the same firm; Category 2 by a separate team in the same organisation; and Category 3 by a wholly independent organisation for the most complex or high-risk works. The TWC assigns the category for each item and ensures the corresponding independent check is completed and signed before loading. Getting the category right is central to a defensible temporary works procedure.",
      },
    ],
  },
};
